import * as THREE from 'three'
import type { GSplatClipState } from '@/plugins/gsplat/clipping/GSplatClipState'
import type { IGSplatNode } from '@/plugins/gsplat/types/LODTypes'
import type { IGSplatResource } from '@/plugins/gsplat/types/ResourceTypes'
import { getLoggerManager } from '@/shared/logging'
import {
  type CompiledGSplatWorkBufferModifier,
  GSPLAT_WORKBUFFER_UPDATE_AUTO,
  type GSplatWorkBufferUpdateMode,
} from '@/shared/types/gsplat'
import type { GSplatOctree } from './GSplatOctree'

const log = getLoggerManager().getLogger('gsplat:lod')

/**
 * 区间类型定义（对应 PlayCanvas 的 Vec2）
 * 表示一个闭区间 [start, end]
 */
export interface Interval {
  /** 区间起点（含） */
  start: number
  /** 区间终点（含） */
  end: number
}

/**
 * GSplat 资源在场景中的放置信息
 *
 * 核心功能：
 * 1. 关联 GSplatResource/GSplatSogsResource 和场景节点 (Object3D)
 * 2. 管理 LOD 级别和 Splat 预算
 * 3. 存储本地空间 AABB 和节点区间
 *
 * @remarks
 * 对应 PlayCanvas 的 GSplatPlacement 类
 * 主要差异：GraphNode → Object3D，BoundingBox → Box3
 */
export class GSplatPlacement {
  /**
   * 关联的 GSplat 资源（可能是 GSplatResource 或 GSplatSogsResource）
   */
  resource: IGSplatResource | null

  /**
   * 场景节点（替代 PlayCanvas 的 GraphNode）
   */
  node: IGSplatNode

  /**
   * 关联的八叉树（仅用于八叉树资源）
   */
  octree: GSplatOctree | null = null

  /**
   * 节点区间映射表
   * Key: 八叉树节点索引
   * Value: 闭区间 [start, end]（包含 start 和 end）
   *
   * @remarks
   * 用于八叉树 LOD：每个八叉树节点关联一段 splat 索引区间
   */
  intervals: Map<number, Interval> = new Map()

  /**
   * LOD 级别索引
   */
  lodIndex: number = 0

  /**
   * LOD 距离阈值数组（仅用于八叉树资源）
   *
   * @remarks
   * - 对于有 N 级 LOD 的八叉树，需要 N-1 个阈值
   * - 非八叉树资源时保持 null
   */
  private _lodDistances: number[] | null = null

  /**
   * LOD 0 到 LOD 1 的基础切换距离。
   */
  private _lodBaseDistance = 5

  /**
   * 连续 LOD 切换距离的几何倍率。
   */
  private _lodMultiplier = 3

  /**
   * Splat 预算（目标渲染的 splat 数量）
   * 0 表示禁用（默认值）
   */
  splatBudget: number = 0

  /** unified LOD 叶节点粗裁使用的最新裁切快照。 */
  clipState: GSplatClipState | null = null

  /**
   * PlayCanvas 风格 WorkBuffer modifier。
   *
   * 仅影响 unified LOD WorkBuffer 写入后的数据，不会反向同步 sorter、LOD、
   * visibility compact、bounds 或 picking 输入。
   */
  workBufferModifier: CompiledGSplatWorkBufferModifier | null = null

  /** WorkBuffer 刷新模式，对齐 PlayCanvas WORKBUFFER_UPDATE_*。 */
  workBufferUpdateMode: GSplatWorkBufferUpdateMode = GSPLAT_WORKBUFFER_UPDATE_AUTO

  /** LOD Environment 的资产实例级期望可见状态。 */
  environmentVisible: boolean = true

  /** 环境资源实例，不参与主体标量直方图统计。 */
  isEnvironment: boolean = false

  /**
   * 本地空间轴对齐包围盒
   */
  private _aabb: THREE.Box3 = new THREE.Box3()

  /**
   * 创建新的 GSplat 放置实例
   *
   * @param resource - GSplat 资源（可为 null）
   * @param node - 场景节点
   * @param lodIndex - LOD 级别（默认 0）
   */
  constructor(resource: IGSplatResource | null, node: IGSplatNode, lodIndex: number = 0) {
    this.resource = resource
    this.node = node
    this.lodIndex = lodIndex
  }

  /**
   * 设置本地空间 AABB
   */
  set aabb(aabb: THREE.Box3) {
    this._aabb.copy(aabb)
  }

  /**
   * 获取本地空间 AABB
   */
  get aabb(): THREE.Box3 {
    return this._aabb
  }

  /**
   * 设置 LOD 距离阈值
   *
   * @remarks
   * - 仅对八叉树资源有效
   * - 数组会被复制（防止外部修改）
   * - 对于有 N 级 LOD 的八叉树，至少需要 N-1 个元素
   */
  set lodDistances(distances: number[] | null) {
    // 检查资源是否为八叉树类型（通过 SogsData 的 meta 判断）
    const isOctree = !!(this.resource && 'meta' in this.resource)

    if (isOctree) {
      if (distances) {
        // PlayCanvas 的验证逻辑
        if (!Array.isArray(distances)) {
          log.warn('lodDistances must be an array')
          return
        }

        // 复制数组以避免外部修改
        this._lodDistances = distances.slice()
      } else {
        this._lodDistances = null
      }
    }
  }

  /**
   * 获取 LOD 距离阈值的副本
   *
   * @returns 阈值数组的副本，或 null（未设置时）
   */
  get lodDistances(): number[] | null {
    return this._lodDistances ? this._lodDistances.slice() : null
  }

  set lodBaseDistance(value: number) {
    if (Number.isFinite(value) && value > 0) {
      this._lodBaseDistance = value
    }
  }

  get lodBaseDistance(): number {
    return this._lodBaseDistance
  }

  set lodMultiplier(value: number) {
    if (Number.isFinite(value) && value > 1) {
      this._lodMultiplier = value
    }
  }

  get lodMultiplier(): number {
    return this._lodMultiplier
  }

  /**
   * 获取世界空间 AABB。通过应用节点的世界变换矩阵计算，会强制更新节点的世界矩阵。
   *
   * @returns 世界坐标系下新创建的包围盒副本
   */
  getWorldAabb(): THREE.Box3 {
    // 确保节点的世界矩阵是最新的
    this.node.updateMatrixWorld(true)

    // 克隆本地 AABB 并应用世界变换
    const worldBox = this._aabb.clone()
    worldBox.applyMatrix4(this.node.matrixWorld)

    return worldBox
  }
}
